import { EventListeners, MinzeElement, Reactive } from 'minze'
import { Language } from '../../../../types/global'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'

Locale.getInstance().registerKeySet('fr', 'LvcPartnerDrafts', {
  title: 'Mes brouillons',
  eventsTitle: 'Événements',
  exhibitionsTitle: 'Expositions',
  festivalsTitle: 'Festivals',
  noDrafts: "Vous n'avez pas de brouillons",
  draftTitle: {
    event: 'Événement sans nom',
    exhibition: 'Exposition sans nom',
    festival: 'Festival sans nom',
  },
})
Locale.getInstance().registerKeySet('en', 'LvcPartnerDrafts', {
  title: 'My Drafts',
  eventsTitle: 'Events',
  exhibitionsTitle: 'Exhibitions',
  festivalsTitle: 'Festivals',
  noDrafts: 'You have no drafts',
  draftTitle: {
    event: 'Untitled event',
    exhibition: 'Untitled exhibition',
    festival: 'Untitled festival',
  },
})

export interface LvcPartnerDrafts {
  draftEvents: any[]
  draftExhibitions: any[]
  draftFestivals: any[]
}

export class LvcPartnerDrafts extends MinzeElement {
  languages: Language[] = []
  reactive: Reactive = [
    ['draftEvents', []],
    ['draftExhibitions', []],
    ['draftFestivals', []],
  ]

  handleAuthChanged = async (data: any): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const isPartner = await mainModule.getAuth().isPartner()
    if (data?.isLoggedIn && isPartner) {
      this.draftEvents = await mainModule.getAuth().fetchDraftEvents()
      this.draftExhibitions = await mainModule.getAuth().fetchDraftExhibitions()
      this.draftFestivals = await mainModule.getAuth().fetchDraftFestivals()
      this.rerender()
    }
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    const authHandler = this.handleAuthChanged.bind(this)
    mainModule.getEE().on('lvc:user_state_changed', authHandler)
    this.handleAuthChanged({
      isLoggedIn: await mainModule.getAuth().isLoggedIn(),
    })
  }

  // Parse JSON string for languages
  parseLanguages(languages: any, existingEvent: boolean): string {
    if (!languages || languages.length === 0) return ''

    try {
      if (existingEvent) {
        let parsedLanguage =
          languages == 'fr'
            ? Locale.getKey('general.languages.fr')
            : Locale.getKey('general.languages.en')
        return parsedLanguage
      }

      return JSON.parse(languages)
        .map((lang: any) => lang.labelFr || lang.labelEn)
        .join(', ')
    } catch (error) {
      console.error('Error parsing languages:', error)
      return ''
    }
  }

  parseAudiences(audiences: any): string {
    if (!audiences || audiences.length === 0) return ''
    // sometimes the audiences is an array when modyfying an existing event
    if (Array.isArray(audiences)) {
      audiences = audiences.join(', ')
      return audiences
    }
    // but most times it is a JSON string
    try {
      return JSON.parse(audiences)
        .map((audience: any) => audience.label)
        .join(', ')
    } catch (error) {
      console.error('Error parsing audiences:', error)
      return ''
    }
  }

  parseDisciplines(disciplines: any): string {
    if (!disciplines || disciplines.length === 0) return ''
    try {
      return JSON.parse(disciplines)
        .map((discipline: any) => discipline.labelFr || discipline.labelEn)
        .join(', ')
    } catch (error) {
      console.error('Error parsing disciplines:', error)
      return ''
    }
  }

  /* when this function receives the event that a draft has been deleted, it will remove it from the UI */
  onDraftDeleted = (event: Event) => {
    const eventData = (event as CustomEvent).detail
    const draftId = eventData.draftId
    const dataType = eventData.dataType
    if (dataType === 'exhibition') {
      this.draftExhibitions = this.draftExhibitions.filter((d: any) => d.id !== draftId)
    } else if (dataType === 'festival') {
      this.draftFestivals = this.draftFestivals.filter((d: any) => d.id !== draftId)
    } else {
      this.draftEvents = this.draftEvents.filter((d: any) => d.id !== draftId)
    }
    this.rerender()
  }

  // Render a draft card for an event or exhibition
  renderDraftCard(draft: any, type: 'event' | 'exhibition' | 'festival'): string {
    let name = ''
    let languages = ''
    let audiences = ''
    let disciplines = ''
    let lastModified = ''

    // For events (or festivals)
    if (type === 'event' || type === 'festival') {
      // Check if we are modifying an existing event: if yes,
      // use the eventData from createEventSearchOrAdd
      if (draft.draftData?.control_modifyingExistingEvent) {
        const eventData = draft.draftData?.createEventSearchOrAdd?.eventData || {}
        name = eventData?.nameFr || ''
        // language for existing event is just a string of either "fr" or "en", so parse it to get the label
        languages = this.parseLanguages(eventData?.inLanguage, true)
        // Assuming that the eventData provides audiences in a similar structure
        audiences = this.parseAudiences(eventData?.audience || eventData?.audiences)
        disciplines = this.parseDisciplines(eventData?.disciplines)
        lastModified = draft?.lastModified
      } else {
        // Otherwise, use the standard createEventDescription values
        name = draft.draftData?.createEventDescription?.eventNameFr || ''
        languages = this.parseLanguages(draft.draftData?.createEventDescription?.languages, false)
        audiences = this.parseAudiences(draft.draftData?.createEventDescription?.audiences)
        disciplines = this.parseDisciplines(draft.draftData?.createEventDescription?.disciplines)
        lastModified = draft?.lastModified
      }
    } else {
      // For exhibitions
      name = draft.draftData?.createExhibitionDescription?.eventNameFr || ''
      languages = this.parseLanguages(
        draft.draftData?.createExhibitionDescription?.languages,
        false
      )
      audiences = this.parseAudiences(draft.draftData?.createExhibitionDescription?.audiences)
      disciplines = this.parseDisciplines(draft.draftData?.createExhibitionDescription?.disciplines)
      lastModified = draft?.lastModified
    }

    return `
      <li>
        <lvc-draft-card
          draft-id="${draft.id}"
          name="${name || Locale.getKey(`LvcPartnerDrafts.draftTitle.${type}`)}"
          languages="${languages}"
          audiences="${audiences}"
          disciplines="${disciplines}"
          last-modified="${lastModified}"
          data-type="${type}"
        ></lvc-draft-card>
      </li>
    `
  }

  html = () => {
    const eventsTitle = Locale.getKey('LvcPartnerDrafts.eventsTitle')
    const exhibitionsTitle = Locale.getKey('LvcPartnerDrafts.exhibitionsTitle')
    const festivalsTitle = Locale.getKey('LvcPartnerDrafts.festivalsTitle')
    const noDraftsTitle = Locale.getKey('LvcPartnerDrafts.noDrafts')
    const eventCards = this.draftEvents.map((d: any) => this.renderDraftCard(d, 'event')).join('')
    const exhibitionCards = this.draftExhibitions
      .map((d: any) => this.renderDraftCard(d, 'exhibition'))
      .join('')
    const festivalCards = this.draftFestivals
      .map((d: any) => this.renderDraftCard(d, 'festival'))
      .join('')

    // Check if there are any drafts in any of the arrays
    const hasDrafts =
      this.draftEvents.length > 0 ||
      this.draftExhibitions.length > 0 ||
      this.draftFestivals.length > 0

    return /*html*/ `
      <h1 class="drafts-title">${Locale.getKey('LvcPartnerDrafts.title')}</h1>
      <div class="drafts-container">
        ${
          hasDrafts
            ? `
              ${
                this.draftEvents.length > 0
                  ? `
                    <div class="draft-section event-drafts-section">
                      <div class="section-header">
                        <lvc-event-draft-icon width="40" height="40"></lvc-event-draft-icon>
                        <h1 class="section-title">${eventsTitle}</h1>
                      </div>
                      <ul class="drafts-list">${eventCards}</ul>
                    </div>
                  `
                  : ''
              }
              ${
                this.draftExhibitions.length > 0
                  ? `
                    <div class="draft-section exhibition-drafts-section">
                      <div class="section-header">
                        <lvc-picture-frame-icon data-width="40" data-height="40"></lvc-picture-frame-icon>
                        <h1 class="section-title">${exhibitionsTitle}</h1>
                      </div>
                      <ul class="drafts-list">${exhibitionCards}</ul>
                    </div>
                  `
                  : ''
              }
              ${
                this.draftFestivals.length > 0
                  ? `
                    <div class="draft-section festival-drafts-section">
                      <div class="section-header">
                        <lvc-picture-frame-icon data-width="40" data-height="40"></lvc-picture-frame-icon>
                        <h1 class="section-title">${festivalsTitle}</h1>
                      </div>
                      <ul class="drafts-list">${festivalCards}</ul>
                    </div>
                  `
                  : ''
              }
            `
            : `<div class="no-drafts-section">
            <h1 class="section-title">${noDraftsTitle}
            </h1>
            <lvc-sad-charlie-face-icon data-width="100" data-height="100" data-fill="#1E007D"></lvc-sad-charlie-face-icon>
            </div>`
        }
      </div>
    `
  }

  eventListeners: EventListeners = [[window, 'lvc:draft-deleted', this.onDraftDeleted]]

  css = () => `
    :host {}
    h1.drafts-title {
      margin: 0;
      margin-bottom: 24px;
      color: #1E007D;
      font-size: 2.5rem;
      font-weight: 800;
      line-height: 1.2;
      font-family: inherit;
    }
    lvc-event-draft-icon {
      color: #1E007D;
    }

    .draft-section {
      margin-bottom: 40px;
    }

    .no-drafts-section {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 20px;
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
    }

    .section-title {
      margin: 0;
      color: #1E007D;
      font-size: 2rem;
      font-weight: 800;
      line-height: 1.2;
    }

    .drafts-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 15px;
    }
    @media only screen and (max-width: 991px) {
      h1.drafts-title {
        margin-top: 1rem;
        font-size: 1.625rem;
      }
      .section-title {
        font-size: 1.5rem;
      }
    }
  `
}
